/**
 * LR-539 bulk re-rate math. Pure — no React, importable by the grid and tests.
 *
 * Resolves each row's NEW rate value(s) from the batch input mode, mirrors the
 * backend calculate_total_rate to preview the new total rate, and derives the
 * daily-P&L delta by anchoring to the authoritative backend `daily_earnings`.
 * KEY identity — holds for REBATE ONLY: daily_earnings is linear in total_rate
 * (net = contract_value × (total_rate/100) / day_count × direction, one base), so
 * new_daily = daily_earnings × (new_total_rate / old_total_rate). Anchoring to the
 * backend figure avoids re-deriving CV / day-count / margin on the FE.
 * NOT fee mode: there net = (fee_income − cost_of_collateral) × direction, and the
 * two legs sit on DIFFERENT bases — fee_income on the unmargined par, cost on the
 * margined CV — so net is proportional to total_rate (= fee − benchmark) only at a
 * zero haircut (par == CV). Scaling by total_rate would misstate the fee-row P&L
 * (materially, with margin), so rowDailyDelta returns null for fee (shows "—")
 * rather than a wrong number on a money screen. The Δ RATE preview stays exact.
 */
import type { TradeItem, BulkReRateEntry } from '@/types';
import {
  buildReRatePayload,
  reRateEditableFields,
  reRateMode,
  type ReRateFormValues,
} from '@/services/trades/rerate-mapper';
import { effectiveTradeRates } from '@/services/trades/effective-rate';

type RateValues = Pick<ReRateFormValues, 'benchmark_rate' | 'spread_bps' | 'fee_percent'>;

const num = (v: unknown): number | undefined => {
  if (v === null || v === undefined || v === '') return undefined;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : undefined;
};

const round4 = (v: number): number => Math.round(v * 1e4) / 1e4;

/** Per-row editable overrides (Set-per-row mode). Undefined = leave booked. */
export interface BulkRateOverride {
  benchmark_rate?: number;
  spread_bps?: number;
  fee_percent?: number;
}

export type BulkReRateInputMode = 'set_per_row' | 'adjust_bps';

/** Only `active` trades can be bulk re-rated in v1 (matured = backdated-only,
 *  deferred). Returns a reason string when ineligible, else null. */
export function bulkReRateIneligibleReason(trade: TradeItem): string | null {
  if (trade.status !== 'active') {
    return `Status ${trade.status_display || trade.status} — only active trades can be bulk re-rated.`;
  }
  return null;
}

/**
 * The NEW rate values for a row, resolved from the batch input mode:
 *  - set_per_row → the row's own override (only the fields the user typed)
 *  - adjust_bps  → booked value + N bps on the field the trade's booking moves:
 *      floating rebate → spread_bps + N        (bps add directly)
 *      fixed rebate    → benchmark_rate + N/100 (1 bp = 0.01 %)
 *      fee             → fee_percent + N/100
 * Never returns a spread for a fixed row (R7). Feed the result to buildBulkEntry.
 */
export function resolveRowValues(
  trade: TradeItem,
  mode: BulkReRateInputMode,
  adjustBps: number | null,
  override: BulkRateOverride | undefined,
): RateValues {
  if (mode === 'set_per_row') {
    return {
      benchmark_rate: override?.benchmark_rate,
      spread_bps: override?.spread_bps,
      fee_percent: override?.fee_percent,
    };
  }
  const n = num(adjustBps);
  if (n === undefined) return {};
  const editable = reRateEditableFields(trade);
  if (editable.includes('spread_bps')) {
    // floating rebate — move the spread by N bps directly (spread is integer bps).
    return { spread_bps: (num(trade.spread_bps) ?? 0) + Math.round(n) };
  }
  if (reRateMode(trade) === 'fee') {
    // fee — a percent field, so N bps = N/100 %.
    return { fee_percent: round4((num(trade.fee_percent) ?? 0) + n / 100) };
  }
  // fixed rebate — move the benchmark (the fixed rate) by N/100 %.
  return { benchmark_rate: round4((num(trade.benchmark_rate) ?? 0) + n / 100) };
}

/** Mirror the backend calculate_total_rate over booked ⊕ resolved. */
export function computeNewTotalRate(trade: TradeItem, resolved: RateValues): number | undefined {
  // LR-628: a floating row's booked benchmark is not what it accrues on, so the
  // "New value" the grid previews has to build on the effective one — otherwise
  // the batch preview and the resulting statement quote two different rates.
  const benchmark = resolved.benchmark_rate ?? num(effectiveTradeRates(trade).benchmarkRate);
  if (reRateMode(trade) === 'fee') {
    const fee = resolved.fee_percent ?? num(trade.fee_percent);
    // The pool accrues the cash's interest, so the trade accrues at the fee alone.
    if (trade.pool_accrues_interest) return fee === undefined ? undefined : round4(fee);
    if (fee === undefined || benchmark === undefined) return undefined;
    return round4(fee - benchmark);
  }
  if (benchmark === undefined) return undefined;
  if (trade.rate_type === 'fixed') return round4(benchmark); // no spread on a fixed row
  const spread = resolved.spread_bps ?? num(trade.spread_bps) ?? 0;
  return round4(benchmark + spread / 100);
}

/** Δ daily P&L in the trade's own currency, anchored to backend daily_earnings.
 *  Null when it cannot be derived: fee mode (net is non-linear in total_rate — see
 *  the module header; scaling would misstate it), missing figures, or a zero old
 *  rate (no coefficient to scale from). */
export function rowDailyDelta(trade: TradeItem, newTotalRate: number | undefined): number | null {
  if (reRateMode(trade) === 'fee') return null; // two-base net — not linear in total_rate
  const { totalRate, dailyEarnings } = effectiveTradeRates(trade);
  const oldTotal = num(totalRate);
  const daily = num(dailyEarnings);
  if (newTotalRate === undefined || oldTotal === undefined || daily === undefined) return null;
  if (oldTotal === 0) return null;
  const perRate = daily / oldTotal; // rebate: daily_earnings is linear in total_rate
  return perRate * (newTotalRate - oldTotal);
}

/** Build the wire entry for a row, reusing buildReRatePayload's R7 gate +
 *  diff-only discipline. Returns null when nothing actually moved (so a no-op
 *  row is dropped from the batch rather than 400-ing it). */
export function buildBulkEntry(trade: TradeItem, resolved: RateValues): BulkReRateEntry | null {
  const payload = buildReRatePayload(trade, { effective_date: '', ...resolved });
  const entry: BulkReRateEntry = { position_id: trade.id };
  let moved = false;
  if (payload.benchmark_rate !== undefined) { entry.benchmark_rate = payload.benchmark_rate; moved = true; }
  if (payload.spread_bps !== undefined) { entry.spread_bps = payload.spread_bps; moved = true; }
  if (payload.fee_percent !== undefined) { entry.fee_percent = payload.fee_percent; moved = true; }
  return moved ? entry : null;
}
